import React, { useState } from 'react';
import {
  AgxLabel,
  AgxDivider,
  AgxColumn,
  AgxRow,
  Images,
  AgxBodyText,
  UploadedDocument,
  DocumentTypes,
} from '@urbanx/agx-ui-components';
import { isEqual } from 'lodash-es';
import { useAppSelector } from 'hooks/useAppSelector';
import FileUpload from '../FileUpload/FileUpload';
import './TypedDocumentUpload.scss';

interface TypedDocumentUploadProps {
  id: string;
  title?: string;
  documentConfig: Array<{
    documentType: DocumentTypes;
    description: string;
    required?: boolean;
    readonly?: boolean;
    multipleFiles?: boolean;
  }>;
  defaultValue?: UploadedDocument[];
  onValueChanged: (value: { id: string; value?: UploadedDocument[] }) => void;
  validate?: boolean;
}

const TypedDocumentUpload = ({
  id,
  title,
  documentConfig,
  defaultValue,
  onValueChanged,
  validate,
}: TypedDocumentUploadProps) => {
  const { campaignId } = useAppSelector(state => state.form);
  const documents = defaultValue ?? [];
  const [uploadError, setUploadError] = useState<string | null>(null);
  const updateDocumentDetail = (
    documentType: DocumentTypes,
    updatedDocumentDetails?: UploadedDocument[]
  ) => {
    const duplicateDocuments = documents.filter(
      d =>
        d.documentType !== documentType &&
        updatedDocumentDetails?.some(
          u => u.containerFilePath === d.containerFilePath
        )
    );

    if (duplicateDocuments.length > 0) {
      setUploadError(
        'Cannot overwrite an existing document. Try use a different file name.'
      );
      return;
    }

    setUploadError(null);

    const updatedDocuments = documents
      .filter(d => d.documentType !== documentType)
      .concat(updatedDocumentDetails ?? []);

    const sortByFileName = (docs: UploadedDocument[]) =>
      [...docs].sort((a, b) => a.fileName.localeCompare(b.fileName));

    const sortedUpdatedDocuments = sortByFileName(updatedDocuments);
    const sortedDocuments = sortByFileName(documents);

    if (!isEqual(sortedUpdatedDocuments, sortedDocuments)) {
      onValueChanged({ id, value: updatedDocuments });
    }
  };

  return (
    <AgxColumn extraLargeGap>
      {title && <AgxLabel large>{title}</AgxLabel>}
      {documentConfig.map((file, index) => {
        const {
          documentType,
          description,
          required,
          readonly,
          multipleFiles,
        } = file;

        const matchingDocuments = documents.filter(
          doc => doc.documentType === documentType
        );

        return (
          <React.Fragment key={index}>
            {index !== 0 ? <AgxDivider expanded /> : <></>}
            <FileUpload
              key={index}
              id={index.toString()}
              title={description}
              extensions={['pdf']}
              required={required}
              validate={validate}
              readonly={readonly}
              fileData={matchingDocuments}
              campaignId={campaignId}
              documentType={documentType}
              multiple={multipleFiles}
              onValueChanged={({ value }) =>
                updateDocumentDetail(documentType, value)
              }
            />
          </React.Fragment>
        );
      })}
      {uploadError && (
        <AgxRow fill mediumGap>
          <span>
            <Images.AlertCircle />
          </span>
          <AgxBodyText small>{uploadError}</AgxBodyText>
        </AgxRow>
      )}
    </AgxColumn>
  );
};

export default TypedDocumentUpload;
